import { Tooltip } from '@/components/primitives/Tooltip'
import { selectGpsTrackingProvider, selectUserConfiguration } from '@/features/domain/user'
import { BaseSelectProps } from '@workwave-tidal/core/components/Select'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { form } from '../../formState'
import { type GpsDeviceIdOption } from '../../types'
import { renderOption } from './renderOption'
import { useTexts } from './useTexts'

interface Props {
  gpsDeviceOptions: GpsDeviceIdOption[]
}

export function TrackDriver(props: Props) {
  const { gpsDeviceOptions } = props
  const texts = useTexts()
  const formApi = form.useFormApi()
  const trackDriverField = form.useFormField('trackDriver')
  const trackDriverChecked = trackDriverField.field.value

  const { userType } = useSelector(selectUserConfiguration)
  const trackingProvider = useSelector(selectGpsTrackingProvider)

  const canAssignGpsDevice = useMemo(
    () => Boolean(gpsDeviceOptions.find(opt => opt.preset !== 'divider' && !opt.disabled)),
    [gpsDeviceOptions],
  )

  const trackDriverVisible = userType === 'rm360' && trackingProvider === 'WWGPS'

  useEffect(() => {
    formApi.setFieldVisibleState('trackDriver', trackDriverVisible)
  }, [trackDriverVisible, formApi])

  useEffect(() => {
    formApi.setFieldVisibleState(
      'gpsDeviceId',
      trackDriverChecked && trackingProvider !== 'telematics',
    )
  }, [formApi, trackDriverChecked, trackingProvider])

  const selectProps = useMemo<BaseSelectProps<GpsDeviceIdOption>>(() => {
    return {
      label: texts.labels.gpsDeviceId,
      items: gpsDeviceOptions,
      optionMode: 'custom' as const,
      getLabel: (value: GpsDeviceIdOption) => {
        if (value.preset === 'divider') return ''
        return value.label
      },

      renderOption,
      autocompleteProps: {
        // required since we are using objects so equality is not by reference
        isOptionEqualToValue(value, option) {
          // this will happen if the user click on the x button
          // the x button is hidden but I would keep this check
          // otherwise the component can throw an error since option.value will throw
          if (value === null) return false
          return option.value === value.value
        },
        slotProps: {
          clearIndicator: {
            sx: { display: 'none' },
          },
          popper: {
            sx: { maxWidth: 500 },
          },
        },
      },
    }
  }, [gpsDeviceOptions])

  if (!trackDriverVisible) return null

  return (
    <>
      <Tooltip
        title={texts.tooltips.noAvailableVirtualDevices}
        disabled={canAssignGpsDevice}
        placement="top"
      >
        <form.Checkbox
          name="trackDriver"
          testId="trackDriver"
          label={texts.labels.trackDriver}
          validateOn="blur change"
          disabled={!canAssignGpsDevice}
          helperText={texts.helpers.trackDriver}
        />
      </Tooltip>

      <form.Select
        name="gpsDeviceId"
        testId="gpsDeviceId"
        validateOn="blur change"
        selectProps={selectProps}
      />
    </>
  )
}
